import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Camera, Trash2, Pencil, Check, X, BarChart3, Download,
  Loader2, ChevronDown, ChevronUp, FileText, FileSpreadsheet,
  Presentation, StickyNote
} from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SnapshotCard({ snap, selected, onSelect, onDelete, onRename, onUpdateAnnotations, sym = '$' }) {
  const [editing, setEditing] = useState(false);
  const [editingAnn, setEditingAnn] = useState(false);
  const [name, setName] = useState(snap.name);
  const [ann, setAnn] = useState(snap.annotations || '');
  const s = snap.summary || {};

  return (
    <div className={`border rounded-lg p-2.5 transition-all cursor-pointer ${
      selected ? 'ring-2 ring-violet-400 bg-violet-50/30' : 'hover:border-violet-300'
    }`} onClick={() => onSelect(snap.id)} data-testid={`snapshot-card-${snap.id}`}>
      <div className="flex items-center justify-between mb-1.5">
        {editing ? (
          <div className="flex items-center gap-1 flex-1 mr-2" onClick={e => e.stopPropagation()}>
            <input value={name} onChange={e => setName(e.target.value)} autoFocus
              className="h-5 flex-1 px-1 text-[10px] border rounded focus:outline-none focus:ring-1 focus:ring-violet-300"
              onKeyDown={e => { if (e.key === 'Enter') { onRename(snap.id, name); setEditing(false); } if (e.key === 'Escape') setEditing(false); }}
            />
            <button onClick={() => { onRename(snap.id, name); setEditing(false); }} className="text-emerald-500 hover:text-emerald-700"><Check className="w-3 h-3" /></button>
            <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-600"><X className="w-3 h-3" /></button>
          </div>
        ) : (
          <span className="text-[11px] font-semibold text-[#0F2040] truncate">{snap.name}</span>
        )}
        <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
          {selected && <Badge className="text-[8px] px-1 py-0 bg-violet-100 text-violet-700" variant="outline">Selected</Badge>}
          <button onClick={() => setEditing(true)} className="text-slate-400 hover:text-violet-600 p-0.5" data-testid={`snapshot-edit-name-${snap.id}`}><Pencil className="w-3 h-3" /></button>
          <button onClick={() => onDelete(snap.id)} className="text-slate-400 hover:text-red-600 p-0.5" data-testid={`snapshot-delete-${snap.id}`}><Trash2 className="w-3 h-3" /></button>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-1 text-[9px]">
        <div><span className="text-slate-400">Effort</span><br /><span className="font-semibold text-slate-700">{s.effort?.toLocaleString() || 0}h</span></div>
        <div><span className="text-slate-400">Duration</span><br /><span className="font-semibold text-slate-700">{s.duration || 0}w</span></div>
        <div><span className="text-slate-400">Cost</span><br /><span className="font-semibold text-slate-700">{sym}{(s.cost || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
        <div><span className="text-slate-400">Peak FTE</span><br /><span className="font-semibold text-slate-700">{s.peak_fte || 0}</span></div>
      </div>

      {/* Annotations */}
      <div className="mt-1.5" onClick={e => e.stopPropagation()}>
        {editingAnn ? (
          <div className="space-y-1">
            <RichTextEditor
              value={ann}
              onChange={setAnn}
              rows={2}
              className="text-[10px]"
              placeholder="Add notes/assumptions..."
              compact
              data-testid={`snapshot-annotations-edit-${snap.id}`}
            />
            <div className="flex items-center gap-1 justify-end">
              <button onClick={() => { onUpdateAnnotations(snap.id, ann); setEditingAnn(false); }}
                className="text-emerald-500 hover:text-emerald-700"><Check className="w-3 h-3" /></button>
              <button onClick={() => { setAnn(snap.annotations || ''); setEditingAnn(false); }}
                className="text-slate-400 hover:text-slate-600"><X className="w-3 h-3" /></button>
            </div>
          </div>
        ) : (
          <button onClick={() => setEditingAnn(true)}
            className="w-full text-left text-[9px] text-slate-400 hover:text-violet-600 flex items-center gap-1 py-0.5"
            data-testid={`snapshot-annotations-btn-${snap.id}`}>
            <StickyNote className="w-2.5 h-2.5 shrink-0" />
            {snap.annotations ? (
              <span className="text-slate-600 truncate">
                {snap.annotations
                  .replace(/^#{1,6}\s+/gm, '')                // headings
                  .replace(/(\*\*|__)(.+?)\1/g, '$2')         // bold
                  .replace(/(\*|_)(.+?)\1/g, '$2')            // italic
                  .replace(/`([^`]+)`/g, '$1')                // inline code
                  .replace(/^[\s]*[-*+]\s+/gm, '• ')          // bullets
                  .replace(/\n+/g, ' · ')                     // newlines → · separator
                  .replace(/<[^>]+>/g, '')                    // any stray HTML
                }
              </span>
            ) : (
              <span className="italic">Add notes...</span>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function ComparisonMatrix({ data, onClose, onExport, exporting, exportingFormat, sym = '$' }) {
  if (!data || !data.columns) return null;

  const cols = data.columns;
  const bw = data.best_worst || {};

  const metrics = [
    { label: 'Implementation Type', key: 'implementation_type' },
    { label: 'Hosting Model', key: 'hosting_model' },
    { label: 'Total Effort', key: 'effort', fmt: v => `${v?.toLocaleString() || 0} hrs`, numeric: true },
    { label: 'Duration', key: 'duration', fmt: v => `${v || 0} weeks`, numeric: true },
    { label: 'Total Cost', key: 'cost', fmt: v => `${sym}${(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, numeric: true },
    { label: 'Peak FTE', key: 'peak_fte', fmt: v => (v || 0).toFixed(2), numeric: true },
    { label: 'Components', key: 'component_count', fmt: v => v || '\u2014' },
  ];

  const origCol = cols[0];

  const FORMATS = [
    { key: 'pdf', label: 'PDF', icon: Download },
    { key: 'docx', label: 'Word', icon: FileText },
    { key: 'xlsx', label: 'Excel', icon: FileSpreadsheet },
    { key: 'pptx', label: 'PPT', icon: Presentation },
  ];

  return (
    <div className="border rounded-lg overflow-hidden" data-testid="snapshot-matrix">
      <div className="bg-[#0F2040] px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-white" />
          <span className="text-xs font-semibold text-white">Scenario Comparison Matrix</span>
          <span className="text-[10px] text-slate-300">{data.estimate_name}</span>
        </div>
        <div className="flex items-center gap-1">
          {FORMATS.map(f => (
            <Button key={f.key} variant="ghost" size="sm"
              onClick={() => onExport(f.key)}
              disabled={exporting}
              className="h-6 px-2 text-[10px] text-white hover:bg-white/10"
              data-testid={`snapshot-export-${f.key}`}>
              {exporting && exportingFormat === f.key
                ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                : <f.icon className="w-3 h-3 mr-1" />
              }
              {f.label}
            </Button>
          ))}
          <button onClick={onClose} className="text-white/60 hover:text-white ml-1"><X className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead>
            <tr className="bg-slate-100">
              <th className="text-left px-3 py-2 font-semibold text-slate-500 text-[9px] uppercase tracking-wide sticky left-0 bg-slate-100 z-10 min-w-[140px]">Metric</th>
              {cols.map((c, i) => (
                <th key={c.id || c.name || `col-${i}`} className="text-center px-3 py-2 font-semibold text-[9px] uppercase tracking-wide min-w-[120px]">
                  <span className={i === 0 ? 'text-slate-600' : 'text-violet-600'}>{c.name}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map(m => (
              <tr key={m.key} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="px-3 py-1.5 font-medium text-slate-600 sticky left-0 bg-white z-10">{m.label}</td>
                {cols.map((c, i) => {
                  const val = c[m.key];
                  const display = m.fmt ? m.fmt(val) : (val || '\u2014');
                  const isBest = m.numeric && bw[m.key]?.best_idx === i;
                  const isWorst = m.numeric && bw[m.key]?.worst_idx === i && cols.length > 2;
                  return (
                    <td key={`${m.key}-${c.id || c.name || i}`} className={`px-3 py-1.5 text-center tabular-nums ${
                      isBest ? 'bg-emerald-50 text-emerald-700 font-semibold' :
                      isWorst ? 'bg-red-50 text-red-600' :
                      i === 0 ? 'text-slate-700' : 'text-[#0F2040] font-medium'
                    }`}>
                      {display}
                      {isBest && <span className="ml-1 text-[8px]">Best</span>}
                    </td>
                  );
                })}
              </tr>
            ))}

            {/* Delta rows */}
            {cols.length > 1 && (
              <>
                <tr className="bg-slate-100">
                  <td className="px-3 py-1.5 font-semibold text-slate-500 text-[9px] uppercase sticky left-0 bg-slate-100 z-10" colSpan={1}>Delta vs Original</td>
                  {cols.map((c, i) => (
                    <td key={`delta-col-${c?.id || c?.snapshot_id || i}`} className="px-3 py-1.5 text-center font-semibold text-slate-500 text-[9px] uppercase">
                      {i === 0 ? '\u2014' : 'Delta'}
                    </td>
                  ))}
                </tr>
                {[
                  { label: 'Effort Change', key: 'effort', suffix: 'h' },
                  { label: 'Duration Change', key: 'duration', suffix: 'w' },
                  { label: 'Cost Change', key: 'cost', isCost: true },
                ].map(d => (
                  <tr key={d.label} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 font-medium text-slate-600 sticky left-0 bg-white z-10">{d.label}</td>
                    {cols.map((c, i) => {
                      if (i === 0) return <td key={`${d.key}-${c.id || c.name || i}`} className="px-3 py-1.5 text-center text-slate-400">{'\u2014'}</td>;
                      const delta = (c[d.key] || 0) - (origCol[d.key] || 0);
                      const isPos = delta > 0;
                      const display = d.isCost
                        ? `${isPos ? '+' : ''}${sym}${delta.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                        : `${isPos ? '+' : ''}${typeof delta === 'number' ? delta.toFixed(1) : delta}${d.suffix}`;
                      return (
                        <td key={`${d.key}-${c.id || c.name || i}`} className={`px-3 py-1.5 text-center font-semibold tabular-nums ${isPos ? 'text-red-600' : delta < 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </>
            )}

            {/* Annotations row */}
            {cols.some(c => c.annotations) && (
              <>
                <tr className="bg-amber-50/50">
                  <td className="px-3 py-1.5 font-semibold text-amber-700 text-[9px] uppercase sticky left-0 bg-amber-50/50 z-10" colSpan={1}>
                    <span className="flex items-center gap-1"><StickyNote className="w-3 h-3" />Annotations</span>
                  </td>
                  {cols.map((c, i) => (
                    <td key={`annot-${c.id || c.name || i}`} className="px-3 py-1.5 text-left text-[9px] text-slate-600 max-w-[200px]">
                      {c.annotations || '\u2014'}
                    </td>
                  ))}
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SnapshotManager({ estimateId, estimateName, currentResult, whatIfNarrative, currencySymbol = '$' }) {
  const [open, setOpen] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [saveName, setSaveName] = useState('');
  const [saveAnnotations, setSaveAnnotations] = useState('');
  const [saving, setSaving] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingFormat, setExportingFormat] = useState('');
  const [comparisonData, setComparisonData] = useState(null);

  const fetchSnapshots = useCallback(async () => {
    if (!estimateId) return;
    try {
      const res = await axios.get(`${API_URL}/api/planner/snapshots/${estimateId}`, { withCredentials: true });
      setSnapshots(res.data.snapshots || []);
    } catch { /* ignore */ }
  }, [estimateId]);

  useEffect(() => {
    if (open) fetchSnapshots();
  }, [open, fetchSnapshots]);

  const saveSnapshot = useCallback(async () => {
    if (!saveName.trim() || !currentResult) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/planner/snapshots`, {
        estimate_id: estimateId,
        name: saveName.trim(),
        config: {},
        result: currentResult,
        annotations: saveAnnotations.trim(),
        narrative: whatIfNarrative || null,
      }, { withCredentials: true });
      setSaveName('');
      setSaveAnnotations('');
      toast.success(`Snapshot "${saveName.trim()}" saved`);
      fetchSnapshots();
    } catch {
      toast.error('Failed to save snapshot');
    } finally {
      setSaving(false);
    }
  }, [estimateId, saveName, saveAnnotations, currentResult, whatIfNarrative, fetchSnapshots]);

  const deleteSnapshot = useCallback(async (id) => {
    try {
      await axios.delete(`${API_URL}/api/planner/snapshots/${id}`, { withCredentials: true });
      setSnapshots(prev => prev.filter(s => s.id !== id));
      setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      toast.success('Snapshot deleted');
    } catch {
      toast.error('Failed to delete');
    }
  }, []);

  const renameSnapshot = useCallback(async (id, name) => {
    try {
      await axios.patch(`${API_URL}/api/planner/snapshots/${id}`, { name }, { withCredentials: true });
      setSnapshots(prev => prev.map(s => s.id === id ? { ...s, name } : s));
      toast.success('Renamed');
    } catch {
      toast.error('Failed to rename');
    }
  }, []);

  const updateAnnotations = useCallback(async (id, annotations) => {
    try {
      await axios.patch(`${API_URL}/api/planner/snapshots/${id}`, { annotations }, { withCredentials: true });
      setSnapshots(prev => prev.map(s => s.id === id ? { ...s, annotations } : s));
      toast.success('Annotations updated');
    } catch {
      toast.error('Failed to update annotations');
    }
  }, []);

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }, []);

  const runComparison = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setComparing(true);
    try {
      const res = await axios.post(`${API_URL}/api/planner/snapshots/compare`, {
        estimate_id: estimateId,
        snapshot_ids: [...selectedIds],
      }, { withCredentials: true });
      setComparisonData(res.data);
    } catch {
      toast.error('Failed to generate comparison');
    } finally {
      setComparing(false);
    }
  }, [estimateId, selectedIds]);

  const exportComparison = useCallback(async (format) => {
    if (selectedIds.size === 0) return;
    setExporting(true);
    setExportingFormat(format);
    try {
      const res = await axios.post(`${API_URL}/api/planner/snapshots/compare/export`, {
        estimate_id: estimateId,
        snapshot_ids: [...selectedIds],
        format,
      }, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const ext = { pdf: '.pdf', docx: '.docx', xlsx: '.xlsx', pptx: '.pptx' }[format] || '.pdf';
      a.download = `scenario_comparison_${(estimateName || 'estimate').replace(/\s+/g, '_').slice(0, 30)}${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported`);
    } catch {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
      setExportingFormat('');
    }
  }, [estimateId, selectedIds, estimateName]);

  return (
    <Card className="border-violet-200/40" data-testid="snapshot-manager">
      <CardHeader className="cursor-pointer pb-2 hover:bg-slate-50/50 transition-colors" onClick={() => setOpen(!open)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Camera className="w-4 h-4 text-violet-500" />
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Scenario Snapshots</CardTitle>
            {snapshots.length > 0 && <Badge className="text-[9px] bg-violet-100 text-violet-700 border-violet-200" variant="outline">{snapshots.length}</Badge>}
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </CardHeader>

      {open && (
        <CardContent className="space-y-3 pt-0">
          {/* Save new snapshot */}
          {currentResult && (
            <div className="space-y-2" data-testid="snapshot-save-section">
              <div className="flex items-center gap-2">
                <input value={saveName} onChange={e => setSaveName(e.target.value)} placeholder="Name this scenario..."
                  className="flex-1 h-8 px-3 text-xs border rounded-md focus:outline-none focus:ring-1 focus:ring-violet-300"
                  onKeyDown={e => { if (e.key === 'Enter') saveSnapshot(); }}
                  data-testid="snapshot-name-input" />
                <Button onClick={saveSnapshot} disabled={saving || !saveName.trim()} className="bg-violet-600 hover:bg-violet-700 text-white text-xs h-8 px-3" data-testid="snapshot-save-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Camera className="w-3.5 h-3.5 mr-1" />}
                  Save Snapshot
                </Button>
              </div>
              <textarea value={saveAnnotations} onChange={e => setSaveAnnotations(e.target.value)}
                rows={2} placeholder="Notes / assumptions for this scenario (optional)..."
                className="w-full text-xs p-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-violet-300 resize-none"
                data-testid="snapshot-annotations-input" />
            </div>
          )}

          {/* Saved snapshots */}
          {snapshots.length > 0 ? (
            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {snapshots.map(s => (
                  <SnapshotCard
              sym={currencySymbol} key={s.id} snap={s} selected={selectedIds.has(s.id)}
                    onSelect={toggleSelect} onDelete={deleteSnapshot} onRename={renameSnapshot}
                    onUpdateAnnotations={updateAnnotations} />
                ))}
              </div>

              {/* Compare & Export actions */}
              <div className="flex items-center gap-2 pt-1 border-t flex-wrap">
                <Button onClick={runComparison} disabled={selectedIds.size === 0 || comparing}
                  variant="outline" className="flex-1 text-xs h-8 border-violet-300 text-violet-700 hover:bg-violet-50"
                  data-testid="snapshot-compare-btn">
                  {comparing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <BarChart3 className="w-3.5 h-3.5 mr-1" />}
                  Compare {selectedIds.size > 0 ? `(${selectedIds.size})` : ''} vs Original
                </Button>
                <Button onClick={() => exportComparison('pdf')} disabled={selectedIds.size === 0 || exporting}
                  variant="outline" className="text-xs h-8 px-3 border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="snapshot-export-btn">
                  {exporting && exportingFormat === 'pdf' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                  PDF
                </Button>
                <Button onClick={() => exportComparison('docx')} disabled={selectedIds.size === 0 || exporting}
                  variant="outline" className="text-xs h-8 px-3 border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="snapshot-export-word-btn">
                  {exporting && exportingFormat === 'docx' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileText className="w-3.5 h-3.5 mr-1" />}
                  Word
                </Button>
                <Button onClick={() => exportComparison('xlsx')} disabled={selectedIds.size === 0 || exporting}
                  variant="outline" className="text-xs h-8 px-3 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="snapshot-export-excel-btn">
                  {exporting && exportingFormat === 'xlsx' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />}
                  Excel
                </Button>
                <Button onClick={() => exportComparison('pptx')} disabled={selectedIds.size === 0 || exporting}
                  variant="outline" className="text-xs h-8 px-3 border-orange-300 text-orange-700 hover:bg-orange-50"
                  data-testid="snapshot-export-pptx-btn">
                  {exporting && exportingFormat === 'pptx' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Presentation className="w-3.5 h-3.5 mr-1" />}
                  PPT
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-[10px] text-slate-400 text-center py-3">No snapshots yet. Run a What-If scenario and save it as a snapshot.</p>
          )}

          {/* Matrix view */}
          {comparisonData && (
            <ComparisonMatrix
          sym={currencySymbol} data={comparisonData} onClose={() => setComparisonData(null)}
              onExport={exportComparison} exporting={exporting} exportingFormat={exportingFormat} />
          )}
        </CardContent>
      )}
    </Card>
  );
}
